import { IProgram } from '../../../../domain/core/entities/programEntity';
import IProgramFailure from '../../../../domain/core/failures/program/programFailure';
import { fromProgramFailureFirebaseMapper } from '../../../../domain/mappers/failures/program/firebase/programFailureFirebaseMapper';
import firebase from 'firebase/app';
import IProgramRespository from '../../../../infrastructure/repositories/program/programRepository';
import { firestoreInitialize } from '../../../../config/firebase/client';
import { nanoid } from 'nanoid'
import { fromFirebaseToDocumentData, fromFirebaseMap } from '../../../../domain/mappers/program/firebase/programMapper';
import { IProgramActivity } from '../../../../domain/core/entities/programActivityEntity';

export default class ProgramFirebase implements IProgramRespository {
  async getPrograms(obj: { start?: string; isNext?: string; isPrevious?: string; limit?: number; }): Promise<IProgram[] | IProgramFailure> {
    try {
        let db = firestoreInitialize();

        let query = db.collection("Programs").where("isDelete", "==", false).orderBy('programId');

        if (obj.start && obj.isNext) {
          query = query.startAfter(obj.start);
        }

        if (obj.start && obj.isPrevious) {
          query = query.endAt(obj.start);
        }

        if (obj.isPrevious && obj.limit) {
          query = query.limitToLast(obj.limit ?? 10);
        }

        if (!obj.isPrevious && obj.limit) {
          query = query.limit(obj.limit ?? 10);
        }

        const snapshots = await query.get();

        const programs: IProgram[] = [];

        if (snapshots.docs.length > 0) {
          snapshots.docs.forEach((snapshot: any) => {
            const programMap: IProgram = fromFirebaseMap(snapshot.data());
            programs.push(programMap);
          });
        }

        return programs;
    } catch (error: any) {
        return fromProgramFailureFirebaseMapper(error.code);
    }
  }

  async getProgramsByQuery(obj: { start?: string; isNext?: string; isPrevious?: string; limit?: number; searchQuery: string }): Promise<IProgram[] | IProgramFailure> {
    try {
        let db = firestoreInitialize();

        let query = db.collection("Programs").where("isDelete", "==", false).orderBy('programId');

        if (obj.start && obj.isNext) {
          query = query.startAfter(obj.start);
        }

        if (obj.start && obj.isPrevious) {
          query = query.endAt(obj.start);
        }

        if (obj.isPrevious && obj.limit) {
          query = query.limitToLast(obj.limit ?? 10);
        }

        if (!obj.isPrevious && obj.limit) {
          query = query.limit(obj.limit ?? 10);
        }

        const snapshots = await query.get();

        const programs: IProgram[] = [];

        if (snapshots.docs.length > 0) {
          snapshots.docs.forEach((snapshot: any) => {
            const programMap: IProgram = fromFirebaseMap(snapshot.data());
            programs.push(programMap);
          });
        }

        let programsResults: IProgram[] = [];

        if (programs.length > 0) {
          programs.forEach((program: IProgram) => {
            if (program.name.toLowerCase().includes(obj.searchQuery.toLowerCase()) || program.programCategory.toLowerCase().includes(obj.searchQuery.toLowerCase())) {
              programsResults.push(program);
            }
          });
        }

        if (!obj.isNext && !obj.isPrevious) programsResults = programsResults.slice(0, obj.limit ? obj.limit - 1 : 9);

        if (obj.isNext) {
          const index: number = programsResults.findIndex((programFind: IProgram) => programFind.programId === obj.isNext);
          programsResults = programsResults.slice(index + 1, obj.limit ? index + (obj.limit + 1) : (index + 1) + 10);
        }

        if (obj.isPrevious) {
          const index: number = programsResults.findIndex((programFind: IProgram) => programFind.programId === obj.isPrevious);
          programsResults = programsResults.slice(obj.limit ? index - (obj.limit + 1) : index + 10, index - 1);
        }

        return programsResults;
    } catch (error: any) {
        return fromProgramFailureFirebaseMapper(error.code);
    }
  }

  async getProgramById(programId: string): Promise<IProgram | IProgramFailure> {
    try {
        let db = firestoreInitialize();

        const snapshot = await db.collection("Programs").where("programId", "==", programId).get();

        let program: IProgram = {} as IProgram;

        if (snapshot.docs.length > 0) program = fromFirebaseMap(snapshot.docs[0].data());

        return program;
    } catch (error: any) {
        return fromProgramFailureFirebaseMapper(error.code);
    }
  }

  async createProgram(program: IProgram): Promise<IProgram | IProgramFailure> {
    try {
        let db = firestoreInitialize();

        program.programId = nanoid(11);
        program.validityStartDate = program.isThereValidityConstraint ? firebase.firestore.Timestamp.fromDate(program.validityStartDate as Date ?? new Date()) : null;
        program.validityEndDate = program.isThereValidityConstraint ? firebase.firestore.Timestamp.fromDate(program.validityEndDate as Date ?? new Date()) : null;
        program.createdOn = firebase.firestore.FieldValue.serverTimestamp();

        await db.collection('Programs').doc(program.programId).set(fromFirebaseToDocumentData(program));

        return program;
    } catch (error: any) {
        return fromProgramFailureFirebaseMapper(error.code);
    }
  }

  async createProgramsWithExcel(programs: IProgram[]): Promise<boolean | IProgramFailure> {
    try {
      let db = firestoreInitialize();

      await Promise.all(programs.map(async (program: IProgram) => {
        program.programId = nanoid(11);

        await db.collection("Programs").doc(program.programId).set({
          programId: program.programId,
          programCategory: program.programCategoryId,
          features: program.features,
          status: "draft",
          isDelete: false,
          createdOn: firebase.firestore.FieldValue.serverTimestamp(),
        });

        if (program.programActivities.length > 0) {
            await Promise.all(program.programActivities.map(async (programActivity: IProgramActivity) => {
              programActivity.programActivityId = nanoid(11);

              db.collection("Programs").doc(program.programId).collection("ProgramActivities").doc(programActivity.programActivityId).set({
                programId: program.programId,
                programActivityId: programActivity.programActivityId,
                type: programActivity.type,
                title: programActivity.name,
                description: programActivity.description,
                trigger: programActivity.trigger,
                triggerMeasure: programActivity.triggerMeasure,
                triggerValue: programActivity.triggerValue,
                delayedActivityMessage: programActivity.delayedActivityMessage,
                status: "draft",
                isDelete: false,
                createdOn: firebase.firestore.FieldValue.serverTimestamp(),
            });
          }));
        }
      }));

      return true;
    } catch (error: any) {
        return fromProgramFailureFirebaseMapper(error.code);
    }
  }

  async editProgram(program: IProgram): Promise<IProgram | IProgramFailure> {
    try {
      let db = firestoreInitialize();

      program.updatedOn = firebase.firestore.FieldValue.serverTimestamp();

      await db.collection('Programs').doc(program.programId).update(fromFirebaseToDocumentData(program));

      return program;
    } catch (error: any) {
        return fromProgramFailureFirebaseMapper(error.code);
    }
  }

  async deleteProgram(programId: string): Promise<string | IProgramFailure> {
    try {
      let db = firestoreInitialize();

        await db.collection('Programs').doc(programId).update({
          isDelete: true,
          deletedOn: firebase.firestore.FieldValue.serverTimestamp(),
        });

        return programId;
    } catch (error: any) {
        return fromProgramFailureFirebaseMapper(error.code);
    }
  }

  async editProgramStatus(programId: string, status: string): Promise<string | IProgramFailure> {
    try {
      let db = firestoreInitialize();

        await db.collection('Programs').doc(programId).update({
          status: status,
          updatedOn: firebase.firestore.FieldValue.serverTimestamp(),
        });

        return programId;
    } catch (error: any) {
        return fromProgramFailureFirebaseMapper(error.code);
    }
  }
}
